import React, {useEffect, useState} from "react";
import {IRole, IRoleMembro} from "@models/roles/roles.ts";
import {Box, Button, Chip, Grid, IconButton, Menu, MenuItem} from '@mui/material';
import {BlockOutlined, CheckOutlined, ClearOutlined, UnfoldMore} from "@mui/icons-material";
import {DataGrid, GridColDef, GridRenderCellParams, useGridApiRef} from "@mui/x-data-grid";
import {useSeguranca} from "@contexts/SegurancaContext.tsx";
import {IPagination} from "@localization/siteMap/siteMapInterfaces.ts";
import dayjs from "dayjs";
import {IUsuario} from "@models/conta/IUsuario.ts";
import {useAlert} from "@contexts/hooks/useAlert.ts";
import {DuplicaRoleComponent} from "@components/roles/DuplicaRoleComponent.tsx";
import {UsuarioPesquisa} from "@components/usuario/UsuarioPesquisa.tsx";
import {useRoles} from "@components/roles/hooks/useRoles.ts";
import {IChangeRoleMembro} from "@models/roles/IChangeRoleMembro.ts";

interface RolesComponentDataGridProps {
}

interface MenuOption {
    name: 'Editar' | 'Desativar' | 'Ativar' | 'Fechar' | 'Roles' | "Duplicar";
    displayName?: string;
    action: (a: any) => void;
    depende?: { param: string, value: boolean };
    color?: any /*ColorPaletteProp*/
}

export const RolesComponentDataGrid: React.FC<RolesComponentDataGridProps> = () => {
    const {createAlertFromResponse, addPopUp} = useAlert()

    const menuOptions: MenuOption[] = [
        {name: 'Editar', action: () => console.log('Editar')},
        {
            name: 'Desativar',
            action: () => {
                if (selectedRole)
                    disableRole({ids: [selectedRole.id]}, true).then(x => createAlertFromResponse(x)).finally(() => setPagination({
                        ...pagination,
                        page: 0
                    }))
            },
            color: "danger",
            depende: {param: 'ativo', value: true}
        },
        {
            name: 'Ativar',
            action: () => {
                if (selectedRole)
                    enableRole({ids: [selectedRole.id]}, true).then(x => createAlertFromResponse(x)).finally(() => setPagination({
                        ...pagination,
                        page: 0
                    }))
            },
            color: "success",
            depende: {param: 'ativo', value: false}
        },
        {
            name: "Roles", displayName: "Exibir Roles", action: () => {
                if (selectedRole)
                    addPopUp({
                        title: `Exibindo membros da role '${selectedRole.nome}'`, content: <RolesMembroDisplay
                            pesquisa={selectedRole}/>
                    })
            }
        },
        {
            name: 'Duplicar', displayName: "Duplicar role", action: () => {
                if (selectedRole) {
                    addPopUp({
                        title: 'Duplicando role',
                        content: <DuplicaRoleComponent role={selectedRole}/>
                    })
                }
            }
        },
        {name: 'Fechar', displayName: 'Fechar menu', action: () => handleClose(), color: "primary"}
    ]

    const apiRef = useGridApiRef()
    const [loading, setLoading] = React.useState<boolean>(false)
    const [Roles, setRoles] = React.useState<IRole[]>([])
    const [pagination, setPagination] = React.useState<IPagination>({page: 1, pageSize: 10})
    const [selectedRole, setSelectedRole] = React.useState<IRole | null>(null)
    const [menuAnchor, setMenuAnchor] = React.useState<any>(null)

    const {getRoles, enableRole, disableRole} = useSeguranca()
    const handleClose = () => {
        setMenuAnchor(null)
    }
    const handleSetMenuOptions = (role: IRole) => {
        setSelectedRole(role)
    }


    const columns: GridColDef[] = [
        {field: 'id', headerName: 'ID', minWidth: 100},
        {field: 'nome', headerName: 'Nome', minWidth: 200},
        {field: 'descricao', headerName: 'Descricao', minWidth: 200},
        {
            field: 'isAtivo', headerName: 'Status', minWidth: 100, type: 'boolean',
            renderCell: (params: GridRenderCellParams<IUsuario>) => {
                return params.value ? <CheckOutlined/> : <ClearOutlined color={'error'}/>
            }
        },
        {
            field: 'dataCriacao',
            headerName: 'Criação',
            minWidth: 200,
            renderCell: (params: GridRenderCellParams<IRole>) => {
                return <>{dayjs(params.row.dataCriacao).format('YYYY-MM-DD HH:mm:ss')}</>
            }
        },
        {
            field: 'action',
            headerName: 'Ação',
            minWidth: 100,
            type: 'actions',
            renderCell: (params: GridRenderCellParams<IRole>) => {
                return (
                    <IconButton onClick={(e) => {
                        handleSetMenuOptions(params.row)
                        if (menuAnchor == e.currentTarget)
                            setMenuAnchor(null)
                        else
                            setMenuAnchor(e.currentTarget)
                    }}>
                        <UnfoldMore/>
                    </IconButton>)
            }
        }
    ]

    useEffect(() => {
        setMenuAnchor(null)
        setLoading(true)
        if (pagination.page == 0) {
            setPagination({...pagination, page: 1})
            return
        }
        getRoles({}, pagination).then(res => {
            setRoles(res.items)
            setPagination({...pagination, ...res.pagination})
        }).finally(() => setLoading(false));
    }, [pagination.page, pagination.pageSize]);

    useEffect(() => {
        apiRef.current?.autosizeColumns({
            includeHeaders: true,
            includeOutliers: true
        })
        apiRef.current?.setRowCount((pagination.total ?? 0))
    }, [!loading])

    return (
        <Box>
            <Menu
                id="lock-menu"
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleClose}
            >
                {menuOptions.filter(x => {
                    return x.depende == null || selectedRole && selectedRole[x.depende.param] == x.depende.value
                }).map((option) => (
                    <MenuItem
                        color={option.color ?? 'neutral'}
                        key={option.name}
                        onClick={() => {
                            if (selectedRole) {
                                option.action(selectedRole)
                                setMenuAnchor(null)
                            }
                        }}
                    >
                        {option.displayName ?? option.name}
                    </MenuItem>
                ))}
            </Menu>
            <DataGrid columns={columns}
                      density={'compact'}
                      loading={loading}
                      rows={Roles}
                      apiRef={apiRef}
                      paginationModel={{page: pagination.page - 1, pageSize: pagination.pageSize}}
                      onPaginationModelChange={(p) => {
                          setPagination({...pagination, page: p.page + 1, pageSize: p.pageSize})
                      }}
                      pageSizeOptions={[10]}
                      rowCount={pagination.total}
                      paginationMeta={{hasNextPage: pagination.hasNext}}
                      paginationMode={'server'}
            />
        </Box>

    )
}

interface RolesMembroDataGridProps {
    pesquisa: IRole | IUsuario
}

export const RolesMembroDisplay: React.FC<RolesMembroDataGridProps> = ({pesquisa}) => {
    const [rolesMembro, setRolesMembro] = React.useState<IRoleMembro[]>([])
    const [pagination, setPagination] = React.useState<IPagination>({page: 1, pageSize: 10})
    const tipo = (pesquisa["email"] != null) ? 'usuario' : 'role'
    const apiRef = useGridApiRef()


    const {getRolesMembro, removeMembroRole, addMembroRole} = useSeguranca()
    const {createAlertFromResponse} = useAlert()

    const columns: GridColDef[] = [
        {
            field: 'role.id', headerName: 'ID', minWidth: 100,
            renderCell: (params: GridRenderCellParams<IRoleMembro>) => {
                return <>{params.row.role.id}</>
            }
        },
        {
            field: 'role.nome', headerName: 'Role', minWidth: 200,
            renderCell: (params: GridRenderCellParams<IRoleMembro>) => {
                return <>{params.row.role.nome}</>
            }
        },
        {
            field: 'usuario.nome', headerName: 'Membro', minWidth: 200,
            renderCell: (params: GridRenderCellParams<IRoleMembro>) => {
                return <>{params.row.usuario.nome}</>
            }
        },
        {
            field: 'ativo', headerName: 'Status', minWidth: 100, type: 'boolean',
            renderCell: (params: GridRenderCellParams<IUsuario>) => {
                return params.value ? <CheckOutlined/> : <ClearOutlined color={'error'}/>
            }
        },
        {
            field: 'action',
            headerName: 'Ação',
            minWidth: 100,
            type: 'actions',
            renderCell: (params: GridRenderCellParams<IRoleMembro>) => {
                return (
                    <IconButton onClick={() => {
                        if (params.row.isAtivo) {
                            removeMembroRole([{
                                idRole: params.row.role.id,
                                idUsuario: params.row.usuario.id
                            }]).then(x => createAlertFromResponse(x)).finally(() => setPagination({
                                ...pagination,
                                page: 0
                            }))
                        } else {
                            addMembroRole([{
                                idRole: params.row.role.id,
                                idUsuario: params.row.usuario.id
                            }]).then(x => createAlertFromResponse(x)).finally(() => setPagination({
                                ...pagination,
                                page: 0
                            }))
                        }
                    }
                    }>
                        {params.row.isAtivo ? <BlockOutlined color={'error'}/> : <CheckOutlined color={'success'}/>}
                    </IconButton>)
            }
        }
    ]

    useEffect(() => {
        if (pagination.page == 0) {
            setPagination({...pagination, page: 1})
            return
        }

        setPagination(prev => ({...prev, loading: true}))
        if (pesquisa as IRole) {
            getRolesMembro(tipo == "role" ? {idsRole: [pesquisa.id]} : {idsUsuario: [pesquisa.id]}, pagination).then(res => {
                setRolesMembro(res.items)
                setPagination({...pagination, ...res.pagination})
            }).finally(() => {
                setPagination(prev => ({...prev, loading: false, ignoreCache: false}))
            })
            return
        }
    }, [pagination.page, pagination.ignoreCache == true])

    return (
        <Box>
            <IncluirMembro pesquisa={pesquisa} onSelect={() => {
                setPagination(prev => ({...prev, ignoreCache: true}))
            }}/>
            <DataGrid columns={columns}
                      density={'compact'}
                      loading={pagination.loading}
                      rows={rolesMembro}
                      apiRef={apiRef}
                      getRowId={(row) => 'g' + row.role.id + 'u' + row.usuario.id}
                      paginationModel={{page: pagination.page - 1, pageSize: pagination.pageSize}}
                      onPaginationModelChange={(p) => {
                          setPagination({...pagination, page: p.page + 1, pageSize: p.pageSize})
                      }}
                      pageSizeOptions={[10]}
                      rowCount={pagination.total}
                      paginationMeta={{hasNextPage: pagination.hasNext}}
                      paginationMode={'server'}
            />
        </Box>
    )
}

interface IncluirMembroProps {
    pesquisa: IRole | IUsuario,
    onSelect: () => void
}

const IncluirMembro: React.FC<IncluirMembroProps> = ({pesquisa, onSelect: _onSelect}) => {
    const [selected, setSelected] = useState<IChangeRoleMembro[]>([])
    const {createAlertFromResponse} = useAlert()
    const {addRoleMembro} = useRoles()

    const removeSelected = (item: IChangeRoleMembro) => {
        setSelected([...selected.filter(x => x.usuarioId != item.usuarioId && x.roleId != item.roleId)])
    }

    const handleAddSelected = () => {
        // addRoleMembro({data: }).then(x => createAlertFromResponse(x)).finally(onSelect)

        setSelected([])
    }

    return (
        <Box>
            <UsuarioPesquisa onItemSelected={(it) => {
                if (it != null) {
                    setSelected(prev => ([...prev, {usuarioId: it.id, roleId: pesquisa.id}]))
                }
            }}
                             options={{clearOnSelect: true}}/>
            <Grid container mt={0.5}
                  mb={0.5}
                  spacing={0.5}>
                <Grid size={10}>
                    {selected.length > 0 && selected.map((x) => <Chip key={`${x.usuarioId}${x.roleId}`}
                                                                      label={x.usuarioId + ' - ' + x.roleId}
                                                                         onDelete={() => removeSelected(x)}
                                                                         variant="filled"/>)}
                </Grid>
                <Grid size={2}>
                    <Button fullWidth color={'success'} disabled={selected.length == 0}
                            onClick={handleAddSelected}>Adicionar</Button>
                </Grid>
            </Grid>
        </Box>
    )
}
